import { formatCurrency, formatNumber } from './formatters'
import { getStateFullName } from './states'

interface SalaryData {
  count?: number
  adjusted_salary?: {
    P10?: number
    P50?: number
    P90?: number
  }
  [key: string]: any
}
interface LocationData {
  city?: string
  state?: string
}
interface TableData {
  job_title?: string
  annual_pay?: number
  hourly_pay?: number
  count?: number
  [key: string]: any
}
interface CityData {
  city: string
  state: string
  median_salary: number
  [key: string]: any
}
type AdjustValueFunction = (value: number) => number
export function generateSalaryDescription(
  salaryData: SalaryData | null,
  companyName: string,
  location: LocationData,
  role?: string,
  adjustValueForPeriod?: AdjustValueFunction
): string {
  if (!salaryData) return ''
  const hasRole = !!role
  const hasLocation = !!(location.city || location.state)
  const formattedLocation = formatLocation(location)
  const salaryCount = salaryData?.count || 0
  const adjustValue = (value: number): number => {
    return adjustValueForPeriod ? adjustValueForPeriod(value) : value
  }
  const countText = `<strong>${formatNumber(salaryCount)}</strong> ${
    salaryCount === 1 ? 'salary report' : 'salary reports'
  }`
  const medianSalary = `<strong>${formatCurrency(
    adjustValue(salaryData.adjusted_salary?.P50 || 0)
  )}</strong>`
  const lowSalary = `<strong>${formatCurrency(
    adjustValue(salaryData.adjusted_salary?.P10 || 0)
  )}</strong>`
  const highSalary = `<strong>${formatCurrency(
    adjustValue(salaryData.adjusted_salary?.P90 || 0)
  )}</strong>`
  let description = `<strong>${companyName}</strong> is ${medianSalary}`
  if (hasRole && hasLocation) {
    description = `<strong>${companyName}</strong> pays ${medianSalary} for <strong>${role}</strong> in <strong>${formattedLocation}</strong>`
  } else if (hasRole) {
    description = `<strong>${companyName}</strong> pays ${medianSalary} for <strong>${role}</strong>`
  } else if (hasLocation) {
    description = `<strong>${companyName}</strong> pays ${medianSalary} in <strong>${formattedLocation}</strong>`
  }
  description += `, with most salaries ranging from ${lowSalary} to ${highSalary}.`
  if (hasRole && hasLocation) {
    description += ` Pay can vary based on role, experience, and local cost of living.`
  } else if (hasRole) {
    description += ` Pay can vary based on location, experience level, and job responsibilities.`
  } else if (hasLocation) {
    description += ` Pay can vary based on role and experience level.`
  } else {
    description += ` Compensation can vary depending on job title, experience level, and location.`
  }
  return description
}
function formatLocation(location: LocationData): string {
  if (location.city && location.state) {
    return `${location.city}, ${location.state}`
  }
  if (location.state) {
    return getStateFullName(location.state)
  }
  return ''
}
export function generateCompanySalaryDescription(
  tableData: TableData[] | null,
  companyName: string,
  adjustValueForPeriod?: AdjustValueFunction
): string {
  if (!tableData || tableData.length < 4) {
    return `Explore salary information for various roles at <strong>${companyName}</strong>.`
  }
  const adjustValue = (value: number): number => {
    return adjustValueForPeriod ? adjustValueForPeriod(value) : value
  }
  const sortedRoles = [...tableData].sort(
    (a, b) => adjustValue(a.annual_pay || 0) - adjustValue(b.annual_pay || 0)
  )
  const total = sortedRoles.length
  const entryLevel = sortedRoles[0]
  const midLevel = sortedRoles[Math.floor(total * 0.33)]
  const midHighLevel = sortedRoles[Math.floor(total * 0.66)]
  const highSalary = sortedRoles[total - 1]
  return `Compensation at <strong>${companyName}</strong> often reflects the level of responsibility tied to each role. At the entry level, positions like <strong>${
    entryLevel.job_title
  }</strong> tend to earn approximately <strong>${formatCurrency(
    adjustValue(entryLevel.annual_pay || 0)
  )}</strong>. With a step up in duties, roles such as <strong>${
    midLevel.job_title
  }</strong> typically offer higher pay—around <strong>${formatCurrency(
    adjustValue(midLevel.annual_pay || 0)
  )}</strong>—due to their specialized nature. Moving into higher responsibilities, a <strong>${
    midHighLevel.job_title
  }</strong> can expect a salary near <strong>${formatCurrency(
    adjustValue(midHighLevel.annual_pay || 0)
  )}</strong>, recognizing their expertise. For top-tier, specialized roles like <strong>${
    highSalary.job_title
  }</strong>, earnings may reach up to <strong>${formatCurrency(
    adjustValue(highSalary.annual_pay || 0)
  )}</strong>, reflecting both the advanced skills required and the strategic importance of the position.`
}
export function generateLocationSalaryDescription(
  citiesData: CityData[] | null,
  companyName: string,
  role: string,
  adjustValueForPeriod?: AdjustValueFunction
): string {
  if (!citiesData || citiesData.length < 4) {
    return `Wondering how pay changes based on where you work? Explore average <strong>${role}</strong> salaries at <strong>${companyName}</strong> across the U.S.`
  }
  const adjustValue = (value: number): number => {
    return adjustValueForPeriod ? adjustValueForPeriod(value) : value
  }
  const sortedCities = [...citiesData].sort(
    (a, b) => adjustValue(a.median_salary) - adjustValue(b.median_salary)
  )
  const lowestSalaryCity = sortedCities[0]
  const highestSalaryCity = sortedCities[sortedCities.length - 1]
  return `Where you work at <strong>${companyName}</strong> can also have a noticeable impact on how much you earn. For example, a <strong>${role}</strong> might make around <strong>${formatCurrency(
    adjustValue(lowestSalaryCity.median_salary)
  )}</strong> in <strong>${lowestSalaryCity.city}, ${
    lowestSalaryCity.state
  }</strong>, but that figure can rise to <strong>${formatCurrency(
    adjustValue(highestSalaryCity.median_salary)
  )}</strong> in a higher-cost city like <strong>${highestSalaryCity.city}, ${
    highestSalaryCity.state
  }</strong>. On the other end of the scale, the same role may earn about <strong>${formatCurrency(
    adjustValue(lowestSalaryCity.median_salary)
  )}</strong> in <strong>${lowestSalaryCity.city}, ${
    lowestSalaryCity.state
  }</strong>, while someone in <strong>${highestSalaryCity.city}, ${
    highestSalaryCity.state
  }</strong> could earn as much as <strong>${formatCurrency(
    adjustValue(highestSalaryCity.median_salary)
  )}</strong>. These variations reflect how salaries at <strong>${companyName}</strong> adapt to local market conditions and living costs.`
}
